import { Controller } from "@hotwired/stimulus";

// Keeps the inbox transcript pinned to the bottom as new messages arrive. Attached to the
// `.message-wrap` scroll container. Two triggers, because the newest message can slip under the
// pinned composer two ways:
//   - MutationObserver: a new row is appended into the transcript.
//   - ResizeObserver: the transcript's own height changes — e.g. when a reply posts, the composer is
//     swapped to an empty pending state (transcript grows) and then restored (transcript shrinks),
//     which would otherwise leave the last message hidden behind the composer.
// Only sticks while the reader is already near the bottom, so scrolling up to read history isn't
// interrupted.
export default class extends Controller {
  connect() {
    this.stick = true;
    this.scrollToBottom();

    this.onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = this.element;
      this.stick = scrollHeight - clientHeight - scrollTop < 120;
    };
    this.element.addEventListener("scroll", this.onScroll, { passive: true });

    const stickToBottom = () => {
      if (this.stick) this.scrollToBottom();
    };
    // A new row appended grows the scroll content but not the element's box, so the ResizeObserver
    // alone wouldn't catch it — hence the MutationObserver. Messages arrive as whole rows (turbo
    // appends a rendered partial), not token-by-token, so `childList` on the container suffices; no
    // `subtree`/`characterData` firing per text node.
    this.mutationObserver = new MutationObserver(stickToBottom);
    this.mutationObserver.observe(this.element, { childList: true });
    // The ResizeObserver covers the element's height changing — e.g. the composer empty↔restore swap.
    this.resizeObserver = new ResizeObserver(stickToBottom);
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.element.removeEventListener("scroll", this.onScroll);
  }

  scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight;
  }
}
